import React, { useContext } from 'react';

import EditInStudioOption from 'components/EditInStudioOption';
import PermissionGates from 'components/PermissionGates';
import { isStudioReadable } from 'components/Studio/canvasMapping';
import StudioBlockEditorContext from 'contextProviders/StudioBlockEditorProvider/StudioBlockEditorContext';
import CardContext from 'contexts/CardContext';
import DashboardContext from 'contexts/DashboardContext';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import ReportContext from 'contexts/ReportContext';
import ReportGadgetContext from 'contexts/ReportGadgetContext';
import { Option } from 'kingpin/atoms/ContextMenu';
import ContextMenuContext from 'kingpin/atoms/ContextMenu/ContextMenuContext';
import PERMISSIONS from 'permissionDefinitions';

const EDIT_ICON: Kingpin.Icon = 'edit-outline';

// A gadget's edit option where the account has Studio, gated as the content
// menu's "Edit" is. Studio blocks offer it, and so do classic gadgets in
// place of their classic Edit and Edit Filters. A gadget Studio can read
// opens in Studio's settings modal on the dashboard or report (see
// StudioBlockEditorProvider), saving as Studio would; one it would keep
// Classic opens the dashboard or report in Studio on its block (its canvas
// card).
const EditStudioBlockOption = () => {
  const { i: blockId } = useContext(CardContext);
  const { dashboard, templatedFrom } = useContext(DashboardContext);
  const { report } = useContext(ReportContext);
  const reportGadgetContext = useContext(ReportGadgetContext);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const { openBlockEditor } = useContext(StudioBlockEditorContext);
  const { closeMenu } = useContext(ContextMenuContext);
  const reportGadget = reportGadgetContext?.reportGadget;

  const isEditableInPlace =
    !!openBlockEditor &&
    (reportGadget
      ? isStudioReadable({ kind: 'report', gadget: reportGadget })
      : !!dashboardGadget &&
        isStudioReadable({ kind: 'dashboard', gadget: dashboardGadget }));

  const option = (contentType: Studio.ContentType, id: string) =>
    isEditableInPlace && openBlockEditor ? (
      <Option
        label="Edit"
        icon={EDIT_ICON}
        onClick={() => {
          closeMenu();
          openBlockEditor(blockId);
        }}
        testId="edit-studio-block"
      />
    ) : (
      <EditInStudioOption
        contentType={contentType}
        id={id}
        blockId={blockId}
        icon={EDIT_ICON}
      />
    );

  if (reportGadget) {
    return report ? (
      <PermissionGates.Has
        requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.EDIT_LAYOUT}
      >
        {option('report', report.id)}
      </PermissionGates.Has>
    ) : null;
  }

  return dashboard && !templatedFrom ? (
    <PermissionGates.Has requiredPermission={PERMISSIONS.DASHBOARDS.EDIT}>
      {option('dashboard', dashboard.id)}
    </PermissionGates.Has>
  ) : null;
};

export default EditStudioBlockOption;
